import PropTypes from 'prop-types';
import { memo, useEffect, useState } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { useRef } from 'react';
import { useContext } from 'react';
import { PageSizeContext } from '../context/page/PageSizeContext';
import MODULES from '../../config/constants/modules';
import { SignatureField } from '../../pages/signature/components/viewer-field/SignatureField';
import { DraftField } from '../../pages/draft/components/pdf-viewer/DraftField';

GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.5.136/pdf.worker.min.mjs";

import { LoaderViewer } from './Loaders';
import { STATUS_VIEWER } from '../../config/constants/status';
import { DraftContext } from '../../pages/draft/context/DraftContext';

import '../../ui/styles/components/PDFViewer.css';
import { SignatureContext } from '../../pages/signature/context/SignatureContext';
import { DraftPDFPage } from '../../pages/draft/components/pdf-viewer/DraftPDFPage';
import { SignaturePDFPage } from '../../pages/signature/components/viewer-field/SignaturePDFPage';
//Visualizador del PDF
export const PDFViewer = memo(({status, setStatus, document, fetchDocument, module, viewerRef}) => {

    const [pages, setPages] = useState([]);

    //console.log('[VIEWER CONTAINER] Se reenderizo');

    useEffect(() => {
        if(!document) return;
        
        const renderAllPages = async(file) => {
            const pagesArray = [];
            for (let currentPage = 1; currentPage <= file.numPages; currentPage++) {
                const page = await file.getPage(currentPage);
                pagesArray.push(page);
            }
            //console.log('[VIEWER CONTAINER] Se ha cargado el documento', pagesArray);

            setPages(pagesArray);
        }

        //console.log('[VIEWER CONTAINER] Se ha cargado el documento', document);

        const loadingTask = getDocument(document).promise;
        loadingTask.then((file) => renderAllPages(file).then(() => setStatus(STATUS_VIEWER.LOADED)));
    }, [document, setStatus]);

    return (
        <section 
            ref={viewerRef}
            className="viewer">
            {
                status === STATUS_VIEWER.IDLE
                ? <LoaderViewer hasError={false} />
                : status === STATUS_VIEWER.LOADED
                    ? pages.map((page, index) => {
                        return (
                            <div 
                                key={index}
                                className='viewer__container'>
                                    {
                                        module === MODULES.DRAFT
                                        ? <DraftPDFPage 
                                            id={index} 
                                            isFirstPage={index === 0}
                                            page={page}
                                            />
                                        : <SignaturePDFPage 
                                            id={index} 
                                            isFirstPage={index === 0}
                                            page={page}
                                            />
                                    }
                                <p className='sm-text grey-text mh-3'>Página {index + 1} de {pages.length}</p>
                            </div>
                        );
                    })
                    : <LoaderViewer hasError={true} handleReload={fetchDocument}/>
            }
        </section>
    );
});

const PDFPage = memo(({ id, page, isFirstPage, module}) => {
    const { draftState } = useContext(DraftContext);
    const { signatureState } = useContext(SignatureContext);
    
    const fieldsByPage = module === MODULES.DRAFT ? draftState.draft.fieldsByPage : signatureState.fieldsByPage;
    const { setPageSize } = useContext(PageSizeContext);

    const elementId = `page-${id}`;
    const pageRef = useRef();

    //TODO: Revisar el rendimiento de este evento
    //if (pageRef?.current) console.log(pageRef.current.clientWidth, pageRef.current.clientHeight);

    useEffect(() => {
        if (!isFirstPage) return;
        const handleResizeViewer = () => {
            const currentPage = pageRef.current;

            if (!currentPage) return;
            const {clientWidth, clientHeight} = currentPage;
 
            setPageSize({ pageWidth: clientWidth, pageHeight: clientHeight });
        }
 
        window.addEventListener('resize', handleResizeViewer);
        
        return () => {
            window.removeEventListener('resize', handleResizeViewer);
        }
    },[isFirstPage, setPageSize]);

    //Inicializa el canvas con el contenido del pdf y establece su tamaño
    useEffect(() => {
        const currentPage = pageRef.current;
        const renderPage = async() => {
            if (currentPage && page) {
                const viewport = page.getViewport({scale: 2});
                const canvas = currentPage;
                const context = canvas.getContext('2d');
    
                canvas.height = viewport.height;
                canvas.width = viewport.width;
    
                await page.render({
                    canvasContext: context,
                    viewport
                }).promise;
            }
        }
        renderPage();

        if (!isFirstPage) return;
        const {clientWidth, clientHeight} = currentPage;

        setPageSize({
            pageWidth: clientWidth,
            pageHeight: clientHeight
        });
    }, [isFirstPage, page, setPageSize]);
    
    return (
        <div className='viewer__container'>
            <div className="viewer__page">
                <canvas
                    ref={pageRef}
                    id={elementId}>
                </canvas>
                {
                    module === MODULES.DRAFT 
                    ? fieldsByPage[id] 
                    &&  Object.keys(fieldsByPage[id]).map((fieldId)=>{
                        return (
                            <DraftField
                                key={fieldId}
                                field={fieldsByPage[id][fieldId]}
                            />
                        );
                    })
                    : fieldsByPage[id] 
                    &&  Object.keys(fieldsByPage[id]).map((fieldId)=>{
                        return (
                            <SignatureField 
                                key={fieldId}
                                field={fieldsByPage[id][fieldId]}
                            />
                        );
                    })
                }
            </div>
        </div>
    );
});

PDFPage.displayName = 'Page';
PDFViewer.displayName = 'ViewerContainer';

PDFViewer.propTypes = {
    document: PropTypes.object,
    status: PropTypes.string.isRequired,
    setStatus: PropTypes.func.isRequired,
    fetchDocument: PropTypes.func.isRequired,
    module: PropTypes.string.isRequired,
    viewerRef: PropTypes.object.isRequired,
}

PDFPage.propTypes = {
    id: PropTypes.number.isRequired,
    page: PropTypes.object.isRequired,
    module: PropTypes.string.isRequired,
    isFirstPage: PropTypes.bool,
}